import BaseValidator from '~/composer/composer/lib/validation/BaseValidator';
import ValidationResult from '~/composer/composer/lib/validation/ValidationResult';
import ValidationSuccess from '~/composer/composer/lib/validation/ValidationSuccess';
import ValidationFail from '~/composer/composer/lib/validation/ValidationFail';
import { REQUIRES_TEXT_OR_ATTACHMENT } from '~/composer/composer/lib/validation/utils/validationErrorMessages';
import { Draft, DraftMethods } from '~/composer/composer/entities/Draft';
import { Retweet } from '~/composer/composer/entities/factories';
import twitterText from 'twitter-text';
import { EditorStateProxy } from '../../entities/EditorStateProxy';

export default class TwitterValidator extends BaseValidator {
  protected validateAttachmentOrTextPresence(
    draft: Draft | { text: string } = this.draft,
    index: number | null = null
  ): ValidationResult {
    if (!DraftMethods.hasText(draft) && !DraftMethods.hasAttachment(draft)) {
      return new ValidationFail(
        index !== null
          ? `${REQUIRES_TEXT_OR_ATTACHMENT} for Tweet #${index + 1}`
          : `${REQUIRES_TEXT_OR_ATTACHMENT}`
      );
    }

    if (
      index !== null &&
      (!(draft instanceof Draft) &&
        twitterText.getTweetLength(draft?.text || '')) >
        (this.draft.service.charLimit || 0)
    ) {
      return new ValidationFail(
        `Tweet #${index + 1} can have maximum ${
          this.draft.service.charLimit
        } characters`
      );
    }

    return new ValidationSuccess();
  }

  protected validateFirstThreadedTweetIsNotNativeRetweet(draft: {
    text: string;
    retweet: Retweet | null;
  }): ValidationResult {
    if (draft?.retweet?.tweetId && draft.text.trim() === '') {
      return new ValidationFail(
        'Threads cannot be started by Retweet without text. Please add content to your first Tweet'
      );
    }

    return new ValidationSuccess();
  }

  /**
   * Validate the actual draft, that is when isDraft == true
   * @protected
   */
  protected validateDraft(): ValidationResult[] {
    // non-thread draft
    if (!this.draft.hasThread()) {
      return [this.validateAttachmentOrTextPresence()].filter(
        (message) => typeof message !== typeof ValidationFail
      );
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore thread is known to be available here
    const threadValidation = this.draft.thread?.map((threadedUpdate, i) => {
      return this.activeThreadId === i
        ? this.validateAttachmentOrTextPresence()
        : this.validateAttachmentOrTextPresence(threadedUpdate, i);
    });

    const parentRetweetValidation =
      this.validateFirstThreadedTweetIsNotNativeRetweet(
        this.activeThreadId === 0
          ? {
              text: EditorStateProxy.getPlainText(this.draft.editorState),
              retweet: this.draft.retweet,
            }
          : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore same, thread is known to be here
            this.draft.thread[0]
      );

    threadValidation?.push(parentRetweetValidation);

    return (
      threadValidation?.filter(
        (message) => typeof message !== typeof ValidationFail
      ) ?? []
    );
  }

  /**
   * Validate the actual update, that is when isDraft == false
   * @protected
   */
  protected validateUpdate(): ValidationResult[] {
    return this.validateDraft();
  }
}
